/**
 * Typed error classes for backend error responses + parseApiError dispatcher.
 *
 * Backend (main-api `exception_handlers.py`) returns the lowercase error_code
 * in a field called `error`:
 *
 *   {
 *     "error": "another_task_in_progress",
 *     "message": "User has another task in progress.",
 *     "data": { "running_task_id": "...", "session_id": "..." }
 *   }
 *
 * `parseApiError` accepts both the lowercase `data.error` (server default) and
 * uppercase `data.error_code` (defensive fallback) and dispatches to the
 * appropriate typed Error subclass. UI layers should `try/catch` and use
 * `instanceof` checks instead of inspecting raw axios error fields.
 *
 * Spec §6.2.3
 */

export class AnotherTaskInProgressError extends Error {
  readonly code = "ANOTHER_TASK_IN_PROGRESS" as const;
  constructor(
    public readonly runningTaskId: string,
    public readonly sessionId: string,
    message?: string,
  ) {
    super(message ?? "Another task is already in progress.");
    this.name = "AnotherTaskInProgressError";
  }
}

export class WorkSessionStillActiveError extends Error {
  readonly code = "WORK_SESSION_STILL_ACTIVE" as const;
  constructor(message?: string) {
    super(message ?? "An active work session must be ended first.");
    this.name = "WorkSessionStillActiveError";
  }
}

export class TaskAlreadyResolvedError extends Error {
  readonly code = "TASK_ALREADY_RESOLVED" as const;
  constructor(message?: string) {
    super(message ?? "Task has already been resolved.");
    this.name = "TaskAlreadyResolvedError";
  }
}

export class InProgressReassignBlockedError extends Error {
  readonly code = "IN_PROGRESS_REASSIGN_BLOCKED" as const;
  constructor(
    public readonly runningAssigneeId?: string,
    message?: string,
  ) {
    super(message ?? "Task is in progress — end work session before reassigning.");
    this.name = "InProgressReassignBlockedError";
  }
}

/**
 * 409 OPEN_WORK_SESSION_EXISTS (2026-05-20).
 *
 * Backend rejects clock_out / break_start when the user has an open task
 * work_session. UX: surface the conflicting task to the user (parallel to
 * AnotherTaskInProgress) so they can actively pause/finish it. Backend
 * does NOT silent-close.
 *
 * Payload (parsed from `data.data`):
 *   - runningTaskId
 *   - sessionId
 *   - attemptedAction: "clock_out" | "break_start"
 */
export class OpenWorkSessionExistsError extends Error {
  readonly code = "OPEN_WORK_SESSION_EXISTS" as const;
  constructor(
    public readonly runningTaskId: string,
    public readonly sessionId: string,
    public readonly attemptedAction: string | null,
    message?: string,
  ) {
    super(
      message ??
        "You have an open task work session; pause or finish it first.",
    );
    this.name = "OpenWorkSessionExistsError";
  }
}

/**
 * 409 NOT_CLOCKED_IN (2026-07-25).
 *
 * Backend rejects task `start` / `resume` when the caller has no open
 * attendance (never clocked in today, or already clocked out). Guards the
 * "work session recorded outside shift hours" data corruption at the source.
 *
 * Payload (parsed from `data.data`):
 *   - userId
 *   - attendanceStatus — 当前考勤态(未打卡时后端可能给 null)
 *   - attemptedAction  — "start" | "resume"
 */
export class NotClockedInError extends Error {
  readonly code = "NOT_CLOCKED_IN" as const;
  constructor(
    public readonly userId: string,
    public readonly attendanceStatus: string | null,
    public readonly attemptedAction: string | null,
    message?: string,
  ) {
    super(message ?? "You must clock in before starting or resuming a task.");
    this.name = "NotClockedInError";
  }
}

export class SpecialTaskInvalidOperationError extends Error {
  readonly code = "SPECIAL_TASK_INVALID_OPERATION" as const;
  constructor(message?: string) {
    super(message ?? "Operation not allowed on a special-type task.");
    this.name = "SpecialTaskInvalidOperationError";
  }
}

export class ResolutionReasonResultMismatchError extends Error {
  readonly code = "RESOLUTION_REASON_RESULT_MISMATCH" as const;
  constructor(message?: string) {
    super(message ?? "Reason code is not applicable to the chosen result.");
    this.name = "ResolutionReasonResultMismatchError";
  }
}

export class ResolutionReasonTaskTypeMismatchError extends Error {
  readonly code = "RESOLUTION_REASON_TASK_TYPE_MISMATCH" as const;
  constructor(message?: string) {
    super(message ?? "Reason code is not applicable to this task type.");
    this.name = "ResolutionReasonTaskTypeMismatchError";
  }
}

export class WorkSessionActiveExistsError extends Error {
  readonly code = "WORK_SESSION_ACTIVE_EXISTS" as const;
  constructor(message?: string) {
    super(message ?? "Task already has an active work session.");
    this.name = "WorkSessionActiveExistsError";
  }
}

export class WorkSessionNotActiveError extends Error {
  readonly code = "WORK_SESSION_NOT_ACTIVE" as const;
  constructor(message?: string) {
    super(message ?? "Work session is already closed.");
    this.name = "WorkSessionNotActiveError";
  }
}

export class TaskClosedLockedError extends Error {
  readonly code = "TASK_CLOSED_LOCKED" as const;
  constructor(message?: string) {
    super(message ?? "Task is closed; mutations are not permitted.");
    this.name = "TaskClosedLockedError";
  }
}

export class InvalidStatusTransitionError extends Error {
  readonly code = "INVALID_STATUS_TRANSITION" as const;
  constructor(
    public readonly fromStatus: string,
    public readonly action: string,
    public readonly allowed: string[],
  ) {
    super(`Action ${action} not allowed from status ${fromStatus}.`);
    this.name = "InvalidStatusTransitionError";
  }
}

export class ForbiddenFieldsInPatchError extends Error {
  readonly code = "FORBIDDEN_FIELDS_IN_PATCH" as const;
  constructor(public readonly fields: string[], message?: string) {
    super(message ?? `PATCH body contains forbidden fields: ${fields.join(", ")}`);
    this.name = "ForbiddenFieldsInPatchError";
  }
}

export class NotAssigneeError extends Error {
  readonly code = "NOT_ASSIGNEE" as const;
  constructor(public readonly taskId: string) {
    super("Current user is not the task assignee.");
    this.name = "NotAssigneeError";
  }
}

/**
 * 403 NOT_PRIMARY_ASSIGNEE (2026-05-28).
 *
 * Backend rejects `reopen` (single + batch) when caller is not the task's
 * primary assignee, not the supervisor, and not a superuser. UI 应在 catch
 * 时 surface 提示让用户改用对的角色重试。
 */
export class NotPrimaryAssigneeError extends Error {
  readonly code = "NOT_PRIMARY_ASSIGNEE" as const;
  constructor(message?: string) {
    super(
      message ??
        "Only the primary assignee, supervisor, or admin can reopen this task.",
    );
    this.name = "NotPrimaryAssigneeError";
  }
}

/**
 * 409 CHILD_NOT_TERMINAL (2026-05-28).
 *
 * Backend rejects parent `finish` / `cancel` / `close` when any child support
 * task is still active (status ∉ {completed, cancelled, skipped, closed}).
 * 取代旧 cascade close 设计:helper 必须显式收尾自己的 child task,parent 不能
 * 替决(spec §3.7.3 旧规则已 DEPRECATED)。
 *
 * Payload (parsed from `data.data`):
 *   - activeChildTaskIds: SnowflakeId 字符串数组
 */
/** 一条待收口的物料行 —— 够渲染收口面板，不用再打一轮请求。 */
export interface UnsettledMaterialRow {
  readonly id: string;
  readonly name: string | null;
  readonly status: string | null;
  /**
   * 没有取料点时是 `null`，**不是空串**：要能区分「没有取料点」（引导先选仓）和
   * 「有但没查到」（数据问题）。
   */
  readonly sourceListingId: string | null;
  /**
   * `null` 表示这是一条 **free-text 行**（没有产品、从未产生库存流水）。
   *
   * 它决定「没用上」这个按钮**说什么**：有 product 的 `picked_up` 行 cancel 之后
   * 后端（B11）会转 `pending_return`，货得还回仓库、仓管确认才算完；free-text 行
   * cancel 直接落 `cancelled`，没有任何东西要退。对后者说「要退回仓库」是过度承诺。
   *
   * 后端给的是 `null` 而不是省略键——`null` 本身就是「这是 free-text」这条业务信息。
   */
  readonly productId: string | null;
  /**
   * 原申请量，收口面板「部分用掉」那一栏的默认值（PRD 5D.6）。
   *
   * 后端发的是 JSON number 不是字符串（前端要拿它做减法：用掉 vs 剩余）。拿不到时
   * 是 `null`，此时面板退回「整条二选一」——**不假设一个数量**，猜错会让账面凭空多
   * 出或少掉货。
   */
  readonly quantity: number | null;
  /**
   * 渠道（方案 2026-11-03）。open 行要靠它分「待定怎么拿」（null）和「申请采购、等批准」
   * （purchase）。后端没给就是 null —— 再配合 sourceListingId 判断。
   */
  readonly sourcingChannel?: string | null;
}

/**
 * 409 MATERIALS_NOT_SETTLED —— 料没收口不让 finish 关活（后端 B12 守卫）。
 *
 * 为什么有这道守卫：prd 现存 83 条卡在 `picked_up` 的料行里，74 条（89%）的 task
 * 已经终结——活干完了没人回来收口。守卫补的是这个流程缺口。
 *
 * 错误体带**足够渲染收口面板**的信息（id / 名称 / 状态 / 取料点），拿到就能直接
 * 画，不用再打一轮请求。
 *
 * ⚠️ 错误码必须与后端**逐字**一致。上面 CHILD_TASKS_NOT_RESOLVED 那条注释记着
 * 一次真实事故：历史上只匹配了一个后端从未发过的名字，导致 blocked dialog 从未
 * 弹出、用户撞进死胡同。
 */
export class MaterialsNotSettledError extends Error {
  readonly code = "MATERIALS_NOT_SETTLED" as const;
  constructor(
    public readonly rows: UnsettledMaterialRow[],
    public readonly taskId: string | null,
    message?: string,
  ) {
    super(message ?? `Cannot finish: ${rows.length} material line(s) are still open.`);
    this.name = "MaterialsNotSettledError";
  }
}

/**
 * 409 ASSESSMENT_INCOMPLETE —— 任务绑了评估面、还有面没答，不让完工（房屋评估契约 §1.5）。
 *
 * `missing` 是评估面 code（`pool` / `guest_experience_estimate` …），不是显示名；
 * 文案层按 code 翻成名字（`assessmentAspectLabel`）。前端点完工前已用卡片数据自检，
 * 走到这里 = 前端漏拦或数据已过期，后端是最终闸门。
 */
export class AssessmentIncompleteError extends Error {
  readonly code = "ASSESSMENT_INCOMPLETE" as const;
  constructor(
    public readonly missing: string[],
    message?: string,
  ) {
    super(message ?? "Some assessment aspects are unanswered.");
    this.name = "AssessmentIncompleteError";
  }
}

export class ChildNotTerminalError extends Error {
  readonly code = "CHILD_NOT_TERMINAL" as const;
  constructor(
    public readonly activeChildTaskIds: string[],
    message?: string,
  ) {
    super(
      message ??
        `Cannot finish / cancel / close while ${activeChildTaskIds.length} child task${activeChildTaskIds.length === 1 ? "" : "s"} still active.`,
    );
    this.name = "ChildNotTerminalError";
  }
}

/**
 * 409 DUPLICATE_TURNOVER_TASK (2026-10-26).
 *
 * The stay already has this turnover cleaning or check-in inspection. Nothing
 * was created. The same body with `duplicateReason` creates the second task.
 * Handle it with `sendWithDuplicateReason` (features/tasks/duplicateTurnover.ts).
 *
 * Payload (the API sends it as `details`):
 *   - existingTaskIds
 *   - reservationId
 */
export class DuplicateTurnoverTaskError extends Error {
  readonly code = "DUPLICATE_TURNOVER_TASK" as const;
  constructor(
    public readonly existingTaskIds: string[],
    public readonly reservationId: string | null,
    message?: string,
  ) {
    super(message ?? "This stay already has this task.");
    this.name = "DuplicateTurnoverTaskError";
  }
}

/**
 * 422 DUPLICATE_TASK_IDS (2026-05-28).
 *
 * Backend rejects batch endpoints when the input `task_ids` array contains
 * duplicates. Envelope-level error (not per-row failure) — the whole batch
 * is refused before any side effect.
 *
 * Payload (parsed from `data.data`):
 *   - duplicateTaskIds: 重复出现的 task id 列表
 */
export class DuplicateTaskIdsError extends Error {
  readonly code = "DUPLICATE_TASK_IDS" as const;
  constructor(
    public readonly duplicateTaskIds: string[],
    message?: string,
  ) {
    super(
      message ??
        `Batch payload contains duplicate task_id${duplicateTaskIds.length === 1 ? "" : "s"}.`,
    );
    this.name = "DuplicateTaskIdsError";
  }
}

/**
 * Attendance 自助打卡错误(web Clock Panel,2026-06-11)。
 * 后端 attendance action 端点的业务校验;多设备(mobile+web)并发时
 * UI 以"提示 + 重拉服务端状态"收敛,不做本地补偿。
 */
export class AttendanceAlreadyClockedInError extends Error {
  readonly code = "ATTENDANCE_ALREADY_CLOCKED_IN" as const;
  constructor(message?: string) {
    super(message ?? "Already clocked in.");
    this.name = "AttendanceAlreadyClockedInError";
  }
}

export class AttendanceInBreakCannotClockOutError extends Error {
  readonly code = "ATTENDANCE_IN_BREAK_CANNOT_CLOCK_OUT" as const;
  constructor(message?: string) {
    super(message ?? "End your break before clocking out.");
    this.name = "AttendanceInBreakCannotClockOutError";
  }
}

export class AttendanceActionTimeTooEarlyError extends Error {
  readonly code = "ATTENDANCE_ACTION_TIME_TOO_EARLY" as const;
  constructor(message?: string) {
    super(message ?? "Action time precedes the latest attendance session.");
    this.name = "AttendanceActionTimeTooEarlyError";
  }
}

/**
 * 409 CONFLICT_ASSET_NOT_DELETABLE (2026-08-23).
 *
 * Asset delete is gated to assets that never lived — anything with history
 * (events past its creation deploy, a linked task, an audit-sheet row, an
 * executed plan) is refused so the archive stays intact. UI must surface the
 * blockers and point the user at Retire instead; there is no force flag.
 *
 * Payload (parsed from `data.data`, snake_case straight off the envelope):
 *   - asset_id / asset_code
 *   - blockers: [{ kind, count, detail }]
 */
export class AssetNotDeletableError extends Error {
  readonly code = "CONFLICT_ASSET_NOT_DELETABLE" as const;
  constructor(
    public readonly assetId: string,
    public readonly assetCode: string | null,
    public readonly blockers: Array<{
      kind: string;
      count: number;
      detail: string[];
    }>,
    message?: string,
  ) {
    super(message ?? "Asset has history and cannot be deleted.");
    this.name = "AssetNotDeletableError";
  }
}

/**
 * Translate a backend error response into a typed Error subclass.
 *
 * Caller is expected to have already verified the response is an error
 * (i.e. `data.error` or `data.error_code` is present).
 *
 * @param status HTTP status code (used for the generic fallback message)
 * @param data   Response body — must carry `error` (lowercase, server default)
 *               or `error_code` (uppercase, defensive). Details live in
 *               `data.data` (server default) or `data.details` (defensive).
 */
/**
 * 这个错误是不是「界面手上的任务状态已过期」—— 后端按**当前**状态拒绝了动作:
 * 状态迁移不合法 / 已到终态 / 已关闭锁定。典型场景:外勤在手机上已处理、另一位
 * 后台员工先点了,而这边的抽屉 / 看板卡片还停在旧状态。
 *
 * 调用方据此**重拉任务**,让按钮与门控回到真相;否则用户只会对着同一个按钮反复
 * 吃同一个 409(2026-09-22 dev 实测)。
 */
export function isTaskStateConflict(err: unknown): boolean {
  return (
    err instanceof InvalidStatusTransitionError ||
    err instanceof TaskAlreadyResolvedError ||
    err instanceof TaskClosedLockedError
  );
}

export function parseApiError(status: number, data: any): Error {
  const err = buildApiError(status, data)
  // 附上 HTTP 状态：typed Error 原本不带 .response/.status，调用方（评论附件失败回收、
  // 重试提示等）无从区分「服务端明确拒绝」与网络层失败（2026-08-28 review）。只补不覆盖。
  if ((err as { status?: unknown }).status === undefined) {
    ;(err as { status?: number }).status = status
  }
  return err
}

function buildApiError(status: number, data: any): Error {
  // 契约文档里的错误体写成 `{ code: "ASSESSMENT_INCOMPLETE", ... }`。拦截器的 translateTypedError 只认
  // error / error_code，这种体会原样 reject；taskActions.executeAction 的 catch 再用 response.data 调到这里，
  // 所以完工这条路也能认出来。只认字符串：数字 code 是外层包装的状态码。
  const code = (data?.error || data?.error_code || (typeof data?.code === "string" ? data.code : "") || "").toString().toUpperCase();
  const details = data?.data ?? data?.details ?? {};
  switch (code) {
    case "ANOTHER_TASK_IN_PROGRESS":
      return new AnotherTaskInProgressError(
        String(details.running_task_id ?? ""),
        String(details.running_session_id ?? details.session_id ?? ""),
        data?.message,
      );
    case "WORK_SESSION_STILL_ACTIVE":
      return new WorkSessionStillActiveError(data?.message);
    case "TASK_ALREADY_RESOLVED":
      return new TaskAlreadyResolvedError(data?.message);
    case "IN_PROGRESS_REASSIGN_BLOCKED":
      return new InProgressReassignBlockedError(
        details.running_assignee_id ? String(details.running_assignee_id) : undefined,
        data?.message,
      );
    case "OPEN_WORK_SESSION_EXISTS":
      return new OpenWorkSessionExistsError(
        String(details.running_task_id ?? ""),
        String(details.running_session_id ?? details.session_id ?? ""),
        typeof details.attempted_action === "string"
          ? details.attempted_action
          : null,
        data?.message,
      );
    case "NOT_CLOCKED_IN":
      return new NotClockedInError(
        String(details.user_id ?? ""),
        typeof details.attendance_status === "string"
          ? details.attendance_status
          : null,
        typeof details.attempted_action === "string"
          ? details.attempted_action
          : null,
        data?.message,
      );
    case "SPECIAL_TASK_INVALID_OPERATION":
      return new SpecialTaskInvalidOperationError(data?.message);
    case "RESOLUTION_REASON_RESULT_MISMATCH":
      return new ResolutionReasonResultMismatchError(data?.message);
    case "RESOLUTION_REASON_TASK_TYPE_MISMATCH":
      return new ResolutionReasonTaskTypeMismatchError(data?.message);
    case "WORK_SESSION_ACTIVE_EXISTS":
      return new WorkSessionActiveExistsError(data?.message);
    case "WORK_SESSION_NOT_ACTIVE":
      return new WorkSessionNotActiveError(data?.message);
    case "TASK_CLOSED_LOCKED":
      return new TaskClosedLockedError(data?.message);
    // 后端**真正在用**的是 TASK_INVALID_STATE_TRANSITION(task_exceptions.
    // TaskInvalidStateTransition,unhold / reopen / close / execute_action /
    // withdraw_helper / start / resume 等 8 处 raise);INVALID_STATUS_TRANSITION
    // 那个类 0 处 raise,是遗留名。2026-09-22 之前这里只认遗留名,真实的 409 一律
    // 掉进 default —— 友好文案 actionError.invalidStatusTransition 从没触发过,
    // 用户看到的是后端内部原文 "Action 'unhold' is not allowed from status 'open'"
    // (dev 实测:抽屉显示 On Hold、别人已先恢复,再点 Resume)。
    //
    // 字段名以后端为准:data = { action, current_status, expected_status };遗留
    // from_status / allowed 保留兼容。details 原样挂回去 —— useReactivateFlow 的
    // reopenAlreadyLanded 读 code / action / current_status 判断「复活其实已落地」。
    case "INVALID_STATUS_TRANSITION":
    case "TASK_INVALID_STATE_TRANSITION": {
      const expected = details.expected_status ?? details.allowed;
      const err = new InvalidStatusTransitionError(
        String(details.current_status ?? details.from_status ?? ""),
        String(details.action ?? ""),
        Array.isArray(expected) ? expected.map(String) : [],
      );
      if (details && typeof details === "object" && Object.keys(details).length > 0) {
        (err as any).details = details;
      }
      return err;
    }
    case "FORBIDDEN_FIELDS_IN_PATCH":
      return new ForbiddenFieldsInPatchError(
        Array.isArray(details.fields) ? details.fields.map(String) : [],
        data?.message,
      );
    case "NOT_ASSIGNEE":
      return new NotAssigneeError(String(details.task_id ?? ""));
    case "NOT_PRIMARY_ASSIGNEE":
      return new NotPrimaryAssigneeError(data?.message);
    // 后端真实错误码是 CHILD_TASKS_NOT_RESOLVED(task_exceptions.py
    // ChildTasksNotResolved,与 mobile 一致)。历史上这里只匹配
    // "CHILD_NOT_TERMINAL"(旧 spec 草案里的名字,后端从未发过)——导致父任务
    // cancel/finish/close 的 409 永远走 raw message 兜底,blocked dialog 从未
    // 弹出(2026-08-12 prod 死胡同事故的一环)。旧名保留作 alias 防御。
    // B12 收口守卫。details 是后端 BusinessException 的 raw dict、不过 schema，
    // 所以字段是 snake_case（与 active_child_task_ids 一致）。
    case "MATERIALS_NOT_SETTLED": {
      const raw = Array.isArray(details.rows) ? details.rows : [];
      return new MaterialsNotSettledError(
        (raw as unknown[]).map((r) => {
          const row = (r ?? {}) as Record<string, unknown>;
          const src = row.source_listing_id ?? row.sourceListingId;
          const prod = row.product_id ?? row.productId;
          const channel = row.sourcing_channel ?? row.sourcingChannel;
          // 🔴 先挡掉 null/空串再 Number()：`Number(null)` 和 `Number("")` 都是
          // **0**（不是 NaN），直接转会把「后端没给数量」变成一条「数量 0」的行
          // ——面板会显示「用掉 0 / 剩余 0」，看起来像一条已经结清的料。
          const rawQty = row.quantity;
          const qty =
            rawQty === null || rawQty === undefined || rawQty === ""
              ? NaN
              : Number(rawQty);
          return {
            // Snowflake 一律字符串 —— 19 位放不进 JS number，转了会静默丢精度
            id: String(row.id ?? ""),
            name: typeof row.name === "string" ? row.name : null,
            status: typeof row.status === "string" ? row.status : null,
            // 只有真的缺才给 null；空串也算缺（别让它变成一个看起来存在的假 id）
            sourceListingId:
              src === null || src === undefined || src === "" ? null : String(src),
            // `null` = free-text 行（没有产品、没有库存流水），它决定「没用上」
            // 那个按钮说什么。同样只有真的缺才给 null。
            productId:
              prod === null || prod === undefined || prod === "" ? null : String(prod),
            // 数量是 JSON number（前端要做减法）。非有限值一律 null —— 面板据此
            // 退回「整条二选一」，而不是拿一个 NaN 去算「剩余多少」。
            quantity: Number.isFinite(qty) ? (qty as number) : null,
            sourcingChannel: typeof channel === "string" && channel ? channel : null,
          };
        }),
        details.task_id != null ? String(details.task_id) : null,
        data?.message,
      );
    }
    case "ASSESSMENT_INCOMPLETE": {
      // HTTPException 的 detail dict 会整个放进 details（{code, message, details: {missing}}），
      // 业务异常则直接是 {missing}；两种都认。
      const raw = details.missing ?? details.details?.missing;
      return new AssessmentIncompleteError(
        Array.isArray(raw) ? raw.map(String) : [],
        data?.message,
      );
    }
    case "CHILD_TASKS_NOT_RESOLVED":
    case "CHILD_NOT_TERMINAL":
      return new ChildNotTerminalError(
        Array.isArray(details.active_child_task_ids)
          ? details.active_child_task_ids.map(String)
          : Array.isArray(details.activeChildTaskIds)
            ? details.activeChildTaskIds.map(String)
            : [],
        data?.message,
      );
    case "DUPLICATE_TASK_IDS":
      return new DuplicateTaskIdsError(
        Array.isArray(details.duplicate_task_ids)
          ? details.duplicate_task_ids.map(String)
          : [],
        data?.message,
      );
    case "DUPLICATE_TURNOVER_TASK": {
      const ids = details.existingTaskIds ?? details.existing_task_ids;
      const reservationId = details.reservationId ?? details.reservation_id;
      return new DuplicateTurnoverTaskError(
        Array.isArray(ids) ? ids.map(String) : [],
        reservationId != null ? String(reservationId) : null,
        data?.message,
      );
    }
    case "CONFLICT_ASSET_NOT_DELETABLE":
      return new AssetNotDeletableError(
        String(details.asset_id ?? details.assetId ?? ""),
        details.asset_code ?? details.assetCode ?? null,
        Array.isArray(details.blockers)
          ? details.blockers.map((b: any) => ({
              kind: String(b?.kind ?? ""),
              count: Number(b?.count ?? 0),
              detail: Array.isArray(b?.detail) ? b.detail.map(String) : [],
            }))
          : [],
        data?.message,
      );
    case "ATTENDANCE_ALREADY_CLOCKED_IN":
      return new AttendanceAlreadyClockedInError(data?.message);
    case "ATTENDANCE_IN_BREAK_CANNOT_CLOCK_OUT":
      return new AttendanceInBreakCannotClockOutError(data?.message);
    case "ATTENDANCE_ACTION_TIME_TOO_EARLY":
      return new AttendanceActionTimeTooEarlyError(data?.message);
    // 其余 attendance 状态机错误码:多设备并发下的终态提示,UI 收敛为
    // "toast + 重拉对齐",无需专用类。固定英文文案——这些码的后端 message
    // 是中文(attendance_service.py:460-547),UI 文案铁律=英文,不可透传;
    // 后端原始 message 由 useClockActions 的 console.error dump 保留诊断。
    case "ATTENDANCE_NOT_CLOCKED_IN":
      return new Error("You are not clocked in.");
    case "ATTENDANCE_ALREADY_CLOCKED_OUT":
      return new Error("Already clocked out (possibly from another device).");
    case "ATTENDANCE_ALREADY_IN_BREAK":
      return new Error("Already on break.");
    case "ATTENDANCE_NOT_IN_BREAK":
      return new Error("Not currently on break.");
    case "ATTENDANCE_ACTION_TIME_DISORDER":
      return new Error("Action time is out of order. Status refreshed — try again.");
    case "ATTENDANCE_NOT_AUTHORIZED":
      return new Error("Not authorized to perform this attendance action.");
    default: {
      const err = new Error(data?.message ?? `Request failed with status ${status}`);
      (err as any).status = status;
      (err as any).code = code || undefined;
      // 保留服务端的 details。没有专用 Error 子类的错误码走这里，其中最常见的
      // 两类**都带字段信息**：
      //   - 400 VALIDATION_FAILED（Service 层 `ValidationError(field=...)`）
      //     → details = { field: "amount" }
      //   - 422 validation_error（Pydantic）
      //     → details = [{ field: "body.transactionDate", message, type }]
      // 拦截器把 400/422 翻译成**没有 `.response` 的** Error 子类，所以这里不带上
      // details，前端就再也拿不到"错在哪个字段"——表单只能显示一句话，无法高亮
      // 出错的输入框。`extractApiErrorField`（src/utils/apiError.ts）读的就是它。
      if (
        details &&
        (Array.isArray(details) ? details.length > 0 : Object.keys(details).length > 0)
      ) {
        (err as any).details = details;
      }
      return err;
    }
  }
}
